import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { LmsStudent } from "../../components/course/types";
import { CheckCircle, Eye, Printer, WandSparkles } from "lucide-react";
import { useToast } from "../../components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";

const IdCardWaitingList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [viewingStudent, setViewingStudent] = useState<LmsStudent | null>(null);
  
  // Sample data - in a real app, this would come from an API
  const [waitingStudents, setWaitingStudents] = useState<LmsStudent[]>([
    {
      id: "1",
      name: "Emily Davis",
      email: "emily.d@example.com",
      lmsAccess: true,
      idCardIssued: false,
      coursesAssigned: "React Fundamentals, Node.js Basics",
      idNumber: "STU12350",
    },
    {
      id: "2",
      name: "Michael Wilson",
      email: "michael.w@example.com",
      lmsAccess: true,
      idCardIssued: false,
      coursesAssigned: "Full Stack Development",
      idNumber: "STU12351",
    },
    {
      id: "3",
      name: "Sarah Brown",
      email: "sarah.b@example.com",
      lmsAccess: false,
      idCardIssued: false,
      coursesAssigned: "Advanced JavaScript",
      idNumber: "STU12352",
    },
  ]);

  const handleGenerateIdCard = (student: LmsStudent) => {
    // Navigate to ID card generation page with student data
    navigate(`/id-card?studentId=${student.id}`);
  };

  const handleSelectStudent = (id: string) => {
    if (selectedStudents.includes(id)) {
      setSelectedStudents(selectedStudents.filter(studentId => studentId !== id));
    } else {
      setSelectedStudents([...selectedStudents, id]);
    }
  };

  const handlePrintSelected = () => {
    if (selectedStudents.length === 0) {
      toast({
        title: "No students selected",
        description: "Please select at least one student to print ID cards.",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Printing ID Cards",
      description: `Preparing to print ${selectedStudents.length} ID cards.`,
    });
  };

  const handleProcessAll = () => {
    setWaitingStudents(waitingStudents.map(student => ({
      ...student,
      idCardIssued: true
    })));

    toast({
      title: "ID Cards Processed",
      description: `All ${waitingStudents.length} ID cards have been processed successfully.`,
    });
  };

  const handleViewDetails = (student: LmsStudent) => {
    setViewingStudent(student);
  };

  return (
    <div className="container py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text -2xl font-bold">ID Card Waiting List</h1>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handlePrintSelected}>
            <Printer className="mr-2" /> Print Selected
          </Button>
          <Button onClick={handleProcessAll}>
            <CheckCircle className="mr-2" /> Process All
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Students Waiting for ID Cards</CardTitle>
          <CardDescription>
            Review and process ID card requests from registered students
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                      checked={selectedStudents.length === waitingStudents.length && waitingStudents.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStudents(waitingStudents.map(s => s.id));
                        } else {
                          setSelectedStudents([]);
                        }
                      }}
                    />
                  </div>
                </TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Courses</TableHead>
                <TableHead>LMS Access</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {waitingStudents.map((student) => (
                <TableRow key={student.id}>
                  <TableCell>
                    <input 
                      type="checkbox" 
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => handleSelectStudent(student.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{student.idNumber}</TableCell>
                  <TableCell>{student.name}</TableCell>
                  <TableCell>{student.email}</TableCell>
                  <TableCell>{student.coursesAssigned}</TableCell>
                  <TableCell>{student.lmsAccess ? "Granted" : "Pending"}</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" onClick={() => handleViewDetails(student)}>
                            <Eye size={16} className="mr-1" /> View
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Student Details</DialogTitle>
                            <DialogDescription>
                              Information about {viewingStudent?.name}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">ID:</span>
                              <span className="col-span-3">{viewingStudent?.idNumber}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Name:</span>
                              <span className="col-span-3">{viewingStudent?.name}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Email:</span>
                              <span className="col-span-3">{viewingStudent?.email}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Courses:</span>
                              <span className="col-span-3">{viewingStudent?.coursesAssigned}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">LMS Access:</span>
                              <span className="col-span-3">
                                {viewingStudent?.lmsAccess ? "Granted" : "Pending"}
                              </span>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Button 
                        variant="default" 
                        size="sm"
                        onClick={() => handleGenerateIdCard(student)}
                      >
                        <WandSparkles size={16} className="mr-1" /> Generate ID
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default IdCardWaitingList;